import { useState, useEffect } from 'react'
import { Company } from '../../types/intellijetnet'
import { AircraftOverride } from '../../types/database'
import { CompaniesService } from '../../services/intellijetnet/companies.service'
import { OverridesService } from '../../services/overrides.service'
import { useOverrideRefresh } from '../../contexts/OverrideRefreshContext'

interface AircraftCompaniesProps {
  aircraftId: number
  override: AircraftOverride | null
}

export default function AircraftCompanies({ aircraftId, override }: AircraftCompaniesProps) {
  const [companies, setCompanies] = useState<Company[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [selectedOwner, setSelectedOwner] = useState<number | null>(override?.owner || null)
  const [selectedBroker, setSelectedBroker] = useState<number | null>(override?.broker || null)
  const [ownerCustom, setOwnerCustom] = useState<string>(override?.owner_custom || '')
  const [brokerCustom, setBrokerCustom] = useState<string>(override?.broker_custom || '')
  const [ownerMode, setOwnerMode] = useState<'company' | 'custom'>(override?.owner_custom ? 'custom' : 'company')
  const [brokerMode, setBrokerMode] = useState<'company' | 'custom'>(override?.broker_custom ? 'custom' : 'company')
  const [error, setError] = useState<string | null>(null)
  const { refreshOverrideForAircraft } = useOverrideRefresh()

  // Update selections when override changes
  useEffect(() => {
    setSelectedOwner(override?.owner || null)
    setSelectedBroker(override?.broker || null)
    setOwnerCustom(override?.owner_custom || '')
    setBrokerCustom(override?.broker_custom || '')
    setOwnerMode(override?.owner_custom ? 'custom' : 'company')
    setBrokerMode(override?.broker_custom ? 'custom' : 'company')
  }, [override])

  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        setLoading(true)
        const companiesData = await CompaniesService.getByAircraftId(aircraftId)
        setCompanies(companiesData)
      } catch (err) {
        setError('Failed to load associated companies')
      } finally {
        setLoading(false)
      }
    }

    if (aircraftId) {
      fetchCompanies()
    }
  }, [aircraftId])

  const handleSave = async () => {
    try {
      setSaving(true)
      setError(null)

      // Prepare override data
      const overrideData = {
        aircraft_id: aircraftId,
        owner: ownerMode === 'company' ? selectedOwner : null,
        broker: brokerMode === 'company' ? selectedBroker : null,
        owner_custom: ownerMode === 'custom' ? ownerCustom.trim() : null,
        broker_custom: brokerMode === 'custom' ? brokerCustom.trim() : null
      }

      // Save the override
      await OverridesService.upsert(overrideData)
      
      // Trigger refresh to update market table
      refreshOverrideForAircraft(aircraftId)
      
    } catch (err) {
      setError('Failed to save company assignments')
    } finally {
      setSaving(false)
    }
  }

  // Check if there are changes to save
  const hasChanges = 
    (ownerMode === 'company' && selectedOwner !== (override?.owner || null)) ||
    (ownerMode === 'custom' && ownerCustom !== (override?.owner_custom || '')) ||
    (brokerMode === 'company' && selectedBroker !== (override?.broker || null)) ||
    (brokerMode === 'custom' && brokerCustom !== (override?.broker_custom || ''))

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Associated Companies</h2>
        <div className="animate-pulse">
          <div className="space-y-3">
            <div className="h-4 bg-gray-200 rounded w-3/4"></div>
            <div className="h-4 bg-gray-200 rounded w-1/2"></div>
            <div className="h-4 bg-gray-200 rounded w-2/3"></div>
          </div>
        </div>
      </div>
    )
  }

  if (companies.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Associated Companies</h2>
        <p className="text-gray-500 text-sm">No companies associated with this aircraft</p>
      </div>
    )
  }

  return (
    <div className="bg-white rounded-lg shadow-sm p-6">
      <h2 className="text-lg font-semibold text-gray-900 mb-4">Associated Companies</h2>
      
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      <div className="space-y-4">
        {/* Owner Selection */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-sm font-medium text-gray-700">
              Owner
            </label>
            <div className="flex items-center space-x-4 text-sm">
              <label className="flex items-center">
                <input
                  type="radio"
                  value="company"
                  checked={ownerMode === 'company'}
                  onChange={(e) => setOwnerMode(e.target.value as 'company' | 'custom')}
                  className="mr-1.5 text-purple-600 focus:ring-purple-500"
                />
                <span className="text-gray-600">Company</span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  value="custom"
                  checked={ownerMode === 'custom'}
                  onChange={(e) => setOwnerMode(e.target.value as 'company' | 'custom')}
                  className="mr-1.5 text-purple-600 focus:ring-purple-500"
                />
                <span className="text-gray-600">Custom</span>
              </label>
            </div>
          </div>
          {ownerMode === 'company' ? (
            <select
              value={selectedOwner || ''}
              onChange={(e) => setSelectedOwner(e.target.value ? parseInt(e.target.value) : null)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
            >
              <option value="">-- Select Owner --</option>
              {companies.map((company) => (
                <option key={company.company_id} value={company.company_id}>
                  {company.company_name}
                </option>
              ))}
            </select>
          ) : (
            <input
              type="text"
              value={ownerCustom}
              onChange={(e) => setOwnerCustom(e.target.value)}
              placeholder="Enter custom owner name"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
            />
          )}
        </div>

        {/* Broker Selection */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-sm font-medium text-gray-700">
              Broker
            </label>
            <div className="flex items-center space-x-4 text-sm">
              <label className="flex items-center">
                <input
                  type="radio"
                  value="company"
                  checked={brokerMode === 'company'}
                  onChange={(e) => setBrokerMode(e.target.value as 'company' | 'custom')}
                  className="mr-1.5 text-purple-600 focus:ring-purple-500"
                />
                <span className="text-gray-600">Company</span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  value="custom"
                  checked={brokerMode === 'custom'}
                  onChange={(e) => setBrokerMode(e.target.value as 'company' | 'custom')}
                  className="mr-1.5 text-purple-600 focus:ring-purple-500"
                />
                <span className="text-gray-600">Custom</span>
              </label>
            </div>
          </div>
          {brokerMode === 'company' ? (
            <select
              value={selectedBroker || ''}
              onChange={(e) => setSelectedBroker(e.target.value ? parseInt(e.target.value) : null)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
            >
              <option value="">-- Select Broker --</option>
              {companies.map((company) => (
                <option key={company.company_id} value={company.company_id}>
                  {company.company_name}
                </option>
              ))}
            </select>
          ) : (
            <input
              type="text"
              value={brokerCustom}
              onChange={(e) => setBrokerCustom(e.target.value)}
              placeholder="Enter custom broker name"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
            />
          )}
        </div>

        {/* Company List */}
        <div className="mt-4">
          <h3 className="text-sm font-medium text-gray-700 mb-2">All Associated Companies</h3>
          
          {/* Show custom values if they exist */}
          {(ownerMode === 'custom' && ownerCustom) || (brokerMode === 'custom' && brokerCustom) ? (
            <div className="mb-3 p-3 bg-gray-50 rounded-md text-sm">
              {ownerMode === 'custom' && ownerCustom && (
                <div className="flex items-center text-purple-600 mb-1">
                  <span className="font-medium">Owner:</span>
                  <span className="ml-2">{ownerCustom}</span>
                  <span className="ml-2 text-xs">(Custom)</span>
                </div>
              )}
              {brokerMode === 'custom' && brokerCustom && (
                <div className="flex items-center text-blue-600">
                  <span className="font-medium">Broker:</span>
                  <span className="ml-2">{brokerCustom}</span>
                  <span className="ml-2 text-xs">(Custom)</span>
                </div>
              )}
            </div>
          ) : null}
          
          <div className="border border-gray-200 rounded-md divide-y divide-gray-200 max-h-48 overflow-y-auto">
            {companies.map((company) => (
              <div key={company.company_id} className="px-3 py-2 text-sm">
                <div className="font-medium text-gray-900">
                  {company.company_name}
                  {ownerMode === 'company' && company.company_id === selectedOwner && (
                    <span className="ml-2 text-xs text-purple-600 font-medium">(Owner)</span>
                  )}
                  {brokerMode === 'company' && company.company_id === selectedBroker && (
                    <span className="ml-2 text-xs text-blue-600 font-medium">(Broker)</span>
                  )}
                </div>
                {company.city && company.state && (
                  <div className="text-xs text-gray-500">
                    {company.city}, {company.state}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Save Button */}
        {hasChanges && (
          <button
            onClick={handleSave}
            disabled={saving}
            className="w-full mt-4 px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors duration-150"
          >
            {saving ? 'Saving...' : 'Save Company Assignments'}
          </button>
        )}
      </div>
    </div>
  )
}